import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import { Box, Select, MenuItem, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { SendOcaLinkSendMethods } from '@trnsact/trnsact-shared-types';
import { MutationResponse } from 'global';
import { sendApplicationFormInitialValues } from 'modules/sendCreditApplication/constants';
import { sendApplicationFormSchema } from 'modules/sendCreditApplication/schema';
import { SendApplicationFormValues } from 'modules/sendCreditApplication/types';
import { ContactDetailsSection } from 'modules/sendCreditApplication/ui/sendApplicationForm/ContactDetailsSection';
import { sendCreditApplicationApi } from 'modules/sendCreditApplication/api';
import { SendOcaLinkVariables } from 'modules/sendCreditApplication/api/types';
import { formatFormValuesToSendOcaLinkPayload } from 'modules/sendCreditApplication/lib';
import { SendFlyerButton } from 'modules/sendCreditApplication/ui/components/SendFlyerButton';
import { useNotifications } from 'modules/notification';
import { lowercaseFirstLetter } from 'formatters';

interface SendEquipmentFlyerFormProps {
  equipmentId: string;
  onSuccess?: () => void;
}

export const SendEquipmentFlyerForm: React.FC<SendEquipmentFlyerFormProps> = ({ equipmentId, onSuccess }) => {
  const form = useForm<SendApplicationFormValues>({
    resolver: zodResolver(sendApplicationFormSchema),
    defaultValues: sendApplicationFormInitialValues,
  });

  const { showNotification } = useNotifications();

  const [sendOcaLink, { loading: isSendOcaLinkLoading }] = useMutation<MutationResponse<string>, SendOcaLinkVariables>(
    sendCreditApplicationApi.sendOcaLink
  );

  const handleSendOcaLink = async (formValues: SendApplicationFormValues) => {
    const input = formatFormValuesToSendOcaLinkPayload(formValues);

    input.flyerMeta.listOfEquipment = [equipmentId];
    input.isPromoteFlyer = true;

    const { data } = await sendOcaLink({ variables: { input } });

    if (!data) {
      showNotification('Failed to send flyer', { type: 'error' });
      return null;
    }

    showNotification('Flyer sent successfully', { type: 'success' });
    onSuccess?.();

    return data?.result?.entity ?? null;
  };

  return (
    <FormProvider {...form}>
      <Box>
        <ContactDetailsSection />

        <Controller
          name="sendMethod"
          control={form.control}
          render={({ field }) => (
            <FormControl
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                mt: 2,
                width: { xs: '100%', md: 'calc((100% - 2rem) / 3)' },
              }}
            >
              <InputLabel id="send-method-select-label">Send by Text and/or Email</InputLabel>
              <Select
                labelId="send-method-select-label"
                id="send-method-select"
                value={field.value}
                onChange={field.onChange}
                input={<OutlinedInput label="Send by Text and/or Email" />}
              >
                {Object.keys(SendOcaLinkSendMethods).map(key => (
                  <MenuItem key={key} value={lowercaseFirstLetter(key)}>
                    {key.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <SendFlyerButton
            variant="contained"
            onClick={form.handleSubmit(handleSendOcaLink)}
            isLoading={isSendOcaLinkLoading}
          />
        </Box>
      </Box>
    </FormProvider>
  );
};
